import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Switch from "../common/Switch";
import { updateAssistantPermissions } from "../../services/eventDetailService";
import { color } from "../../styles/theme";

/**
 * Business Assistant permission editor — ported from mobile's
 * edit-permissions screen (src/Screens/edit-permissions). Opened per-row
 * from the Business Assistants section in ParticipantsTab.jsx instead of a
 * separate route.
 *
 * @param assistant the assistant row object from getBusinessAssistants (has
 *   AssistantId + a nested Permissions object)
 */
const PERMISSION_FIELD_DEFS = [
  { key: "canGenerateQr", apiKey: "CanGenerateQr" },
  { key: "canKickOrBlockUsers", apiKey: "CanKickOrBlockUsers" },
  { key: "canChatAsAssistant", apiKey: "CanChatAsAssistant" },
  { key: "canCreateGalleryAlbum", apiKey: "CanCreateGalleryAlbum" },
  { key: "canViewGallery", apiKey: "CanViewGallery" },
];

const EditPermissionsModal = ({ assistant, onClose, onUpdated }) => {
  const { t } = useTranslation(["eventDetails", "common"]);
  const PERMISSION_FIELDS = PERMISSION_FIELD_DEFS.map((f) => ({ ...f, label: t(`editPermissionsModal.fields.${f.key}`) }));
  const initial = useMemo(
    () => PERMISSION_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: Boolean(assistant?.Permissions?.[f.apiKey]) }), {}),
    [assistant]
  );
  const [values, setValues] = useState(initial);
  const [saving, setSaving] = useState(false);

  const hasChanges = PERMISSION_FIELDS.some((f) => values[f.key] !== initial[f.key]);

  const handleToggle = (key) => {
    const newValue = !values[key];
    if (key === "canCreateGalleryAlbum" && newValue === true && !values.canViewGallery) {
      toast.warning(t("editPermissionsModal.galleryRequiresViewWarning"));
      return;
    }
    setValues((prev) => ({ ...prev, [key]: newValue }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateAssistantPermissions(assistant.AssistantId, values);
      toast.success(res.data?.Message || t("editPermissionsModal.updateSuccess"));
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("editPermissionsModal.updateError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t("editPermissionsModal.title")}
      onClose={onClose}
      busy={saving}
      maxWidth={400}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>{t("common:actions.cancel")}</Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges}>{saving ? t("editPermissionsModal.saving") : t("common:actions.save")}</Button>
        </>
      }
    >
      <div style={s.list}>
        {PERMISSION_FIELDS.map((f, i) => (
          <div key={f.key} style={{ ...s.row, ...(i === PERMISSION_FIELDS.length - 1 ? { borderBottom: "none" } : {}) }}>
            <span style={s.rowLabel}>{f.label}</span>
            <Switch checked={values[f.key]} onChange={() => handleToggle(f.key)} disabled={saving} />
          </div>
        ))}
      </div>
    </Modal>
  );
};

const s = {
  list: { display: "flex", flexDirection: "column" },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 2px", borderBottom: `1px solid ${color.gray100}` },
  rowLabel: { fontSize: 13.5, color: color.gray800, fontWeight: 500 },
};

export default EditPermissionsModal;
